import { USAGE_RANGES } from "../../lib/usageWindow";
import type { CustomWindowInput, UsageWindowState } from "../../lib/usageWindow";
import { DateRangeField } from "../ui/DateField";
import { Field, SelectField } from "../ui/Input";

/**
 * The control that chooses the window the fleet views report energy over —
 * the four presets, or a span the operator picks (server spec §8.3).
 *
 * ## Why it is shared rather than written twice
 *
 * `Fleet` and `Depots` ask the same question of the same parameters, and the
 * answer has four controls and a resolution rule behind it. Two copies would
 * drift on the first change to either, and the failure would be quiet: two pages
 * offering the same picker and computing a different March.
 *
 * ## Why it renders no wrapper
 *
 * A fragment of grid children, so each page's own filter grid places them. The
 * two pages have different column counts and the picker has no business knowing
 * which.
 *
 * ## Why the dates are in the URL
 *
 * Same reason as every filter on those pages: a window an operator chose is a
 * link they can paste into a ticket. Unlike a preset, a chosen span means the
 * same thing in October as it did in August, which is exactly why it may be
 * frozen into a URL while "this month" may not.
 *
 * ## Why the dates are a range and the times are not
 *
 * The two dates are one decision and are now one control, because they were only
 * ever meaningful together — a start with no end is not half a window, it is no
 * window. The times stay two native `<input type="time">`, and stay optional,
 * because each qualifies its own end independently and typing `14:30` into the
 * platform control beats every picker anyone has built for it.
 */

export function UsageWindowPicker({
  range,
  custom,
  state,
  zone,
  onChange,
}: {
  readonly range: string;
  readonly custom: CustomWindowInput;
  readonly state: UsageWindowState;
  /**
   * The calendar the window is drawn in — the depot's, or UTC for a fleet
   * spanning several. Passed through so the grid marks the operator's fleet's
   * today rather than the browser's.
   */
  readonly zone: string;
  /**
   * A batch of URL parameters, empty meaning remove. The caller owns the URL
   * policy — the fleet page resets pagination on a filter write and this
   * component must not know that — and it has to be a batch because choosing a
   * new start date clears the old end in the same click.
   */
  readonly onChange: (updates: Readonly<Record<string, string>>) => void;
}) {
  const isCustom = range === "custom";

  return (
    <>
      {/*
        A filter in shape but not in effect: it adds a column rather than removing
        rows. The hint carries the state, and all three of its cases are load-
        bearing — "off" so a missing column is explained, the pending sentence so
        a half-filled range is explained, and the zone so a resolved figure is
        never unqualified. Without the zone, "This month" is not an interval, and
        two operators in different offices read the same figure as covering
        different days.
      */}
      <SelectField
        label="Energy window"
        value={range}
        hint={hint(state)}
        onChange={(event) => onChange({ range: event.target.value })}
      >
        <option value="">No energy column</option>
        {USAGE_RANGES.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </SelectField>

      {!isCustom ? null : (
        <>
          <DateRangeField
            label="Dates"
            zone={zone}
            from={custom.fromDate ?? ""}
            to={custom.toDate ?? ""}
            onChange={(next) => onChange({ fromDate: next.from, toDate: next.to })}
            // The rule the operator has to be able to see, because the total
            // cannot show it: a day silently excluded looks exactly like a day
            // with no charging.
            hint="Both days counted in full, unless you give times."
          />
          <Field
            label="From time"
            type="time"
            value={custom.fromTime ?? ""}
            onChange={(event) => onChange({ fromTime: event.target.value })}
            hint="Optional. Blank starts at midnight."
          />
          <Field
            label="To time"
            type="time"
            value={custom.toTime ?? ""}
            onChange={(event) => onChange({ toTime: event.target.value })}
            hint="Optional. Exclusive when given."
          />
        </>
      )}
    </>
  );
}

function hint(state: UsageWindowState): string {
  switch (state.kind) {
    case "off":
      return "off";
    case "pending":
      return state.message;
    case "resolved":
      return `${state.window.label} · ${state.window.zone}`;
  }
}
